import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ID, isArray, withTransaction } from '@datorama/akita';
import { tap } from 'rxjs/operators';
import { LedgerAccount } from './ledger-account.model';
import { LedgerAccountsStore } from './ledger-accounts.store';
import { AppConfigQuery } from '../../../../core/state/app-config/app-config.query';
import { OrArray } from '@datorama/akita/src/lib/types';
import { Book } from '../../books/state/book.model';

@Injectable({ providedIn: 'root' })
export class LedgerAccountsService {
    constructor(
        private ledgerAccountsStore: LedgerAccountsStore,
        private appConfig: AppConfigQuery,
        private http: HttpClient
    ) {}

    get(bookId: Book['id']) {
        this.ledgerAccountsStore.setLoading(true);
        return this.http
            .get<LedgerAccount[]>(
                `${this.appConfig.get().accountingBaseUrl}/accounting/v1/ledger-accounts`,
                {
                    params: {
                        bookIds: bookId
                    }
                }
            )
            .pipe(
                withTransaction((entities) => {
                    this.ledgerAccountsStore.set(entities);
                    this.ledgerAccountsStore.setLoading(false);
                })
            );
    }

    add(ledgerAccount: OrArray<Omit<LedgerAccount, 'createdAt'>>) {
        return this.http
            .post<OrArray<LedgerAccount>>(
                `${this.appConfig.get().accountingBaseUrl}/accounting/v1/ledger-accounts`,
                isArray(ledgerAccount) ? ledgerAccount : [ledgerAccount]
            )
            .pipe(
                tap((newLedgerAccount) => {
                    if (isArray(newLedgerAccount)) {
                        this.ledgerAccountsStore.upsertMany(newLedgerAccount);
                    } else {
                        this.ledgerAccountsStore.upsert(
                            newLedgerAccount.id,
                            newLedgerAccount
                        );
                    }
                })
            );
    }

    update(id: LedgerAccount['id'], ledgerAccount: Partial<LedgerAccount>) {
        return this.http
            .put<LedgerAccount>(
                `${
                    this.appConfig.get().accountingBaseUrl
                }/accounting/v1/ledger-account/${id}`,
                ledgerAccount
            )
            .pipe(
                tap((updatedLedgerAccount) => {
                    this.ledgerAccountsStore.update(
                        id,
                        updatedLedgerAccount || ledgerAccount
                    );
                })
            );
    }

    remove(id: LedgerAccount['id']) {
        return this.http
            .delete<LedgerAccount>(
                `${
                    this.appConfig.get().accountingBaseUrl
                }/accounting/v1/ledger-account/${id}`
            )
            .pipe(
                tap((updatedLedgerAccount) => {
                    this.ledgerAccountsStore.remove(id);
                })
            );
    }

    private _removeAll() {
        this.ledgerAccountsStore.set([]);
    }
}
